import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Download,
  MessageSquare,
  Lightbulb,
  Target,
  Brain,
  Users,
  Award,
  ChevronLeft,
  Sparkles,
  Info,
  Code,
  Briefcase,
  FileText,
  TrendingUp,
  Layout,
  Zap,
  AlignLeft,
  BookOpen,
  Wrench,
  Search,
  Edit2,
  Puzzle,
  Shield,
  Video,
  Play,
  X,
  Clock
} from 'lucide-react';
import ResultsClient from '../data/api/ResultsClient';
import Loader from '../../../shared/components/Loader';
import Modal from '../../../shared/components/Modal';
import DownloadReportModal from '../components/DownloadReportModal';
import '../styles/AdminFeedbackPage.css';



type FeedbackTab = "overview" | "hr" | "behavioral" | "hiringManager" | "technical" | "competency" | "resume" | "improvements" | "transcript" | "proctoring" | "video" | "coding";

export interface AdminFeedbackPageProps {
  inlineSessionId?: string;
  inlineCandidateId?: string;
  isInline?: boolean;
  onClose?: () => void;
}

const AdminFeedbackPage: React.FC<AdminFeedbackPageProps> = ({ inlineSessionId, inlineCandidateId, isInline, onClose }) => {
  const calculateDaysRemaining = (createdAt: string) => {
    if (!createdAt) return 0;
    const parts = createdAt.split(', ');
    if (parts.length === 2) {
      const [datePart, timePart] = parts;
      const isoString = `${datePart.replace(/\//g, '-')}T${timePart}`;
      const createdDate = new Date(isoString);
      if (!isNaN(createdDate.getTime())) {
        const expiryDate = new Date(createdDate.getTime() + 30 * 24 * 60 * 60 * 1000);
        const now = new Date();
        const diffTime = expiryDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
      }
    }
    return 30;
  };
  const location = useLocation();
  const scheduleId = location.state?.scheduleId;
  const sessionId = inlineSessionId || location.state?.sessionId;
  const candidateId = inlineCandidateId || location.state?.candidateId;
  const returnTab = location.state?.returnTab;
  const returnPath = location.state?.returnPath;

  const navigate = useNavigate();

  const handleBack = () => {
    if (isInline && onClose) {
      onClose();
      return;
    }
    navigate('/dashboard/results/stats', {
      state: { scheduleId, sessionId, candidateId, returnTab, returnPath }
    });
  };
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<FeedbackTab>("overview");
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [animatedScore, setAnimatedScore] = useState(0);
  const [proctorData, setProctorData] = useState<any>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showVideoNote, setShowVideoNote] = useState(true);

  const handleDownloadReport = async (includeTranscript: boolean) => {
    setShowDownloadModal(false);
    if (!sessionId) return;

    setReportLoading(true);
    try {
      const response = await ResultsClient.getReport(sessionId, candidateId, includeTranscript);
      if (response && response.report_url) {
        window.open(response.report_url, '_blank');
      } else {
        alert("Failed to generate report URL");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("An error occurred while generating your report.");
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadProctoringReport = async () => {
    if (!sessionId) return;
    setReportLoading(true);
    try {
      const response = await ResultsClient.getProctoringReport(sessionId, candidateId);
      if (response && response.proctoring_report_url) {
        window.open(response.proctoring_report_url, '_blank');
      } else {
        alert("Proctoring report not available yet.");
      }
    } catch (error) {
      console.error("Error fetching proctoring report:", error);
      alert("An error occurred while fetching the proctoring report.");
    } finally {
      setReportLoading(false);
    }
  };

  useEffect(() => {
    const fetchFeedback = async () => {
      if (!sessionId) return;
      setLoading(true);
      try {
        const response = await ResultsClient.getFeedback(sessionId, candidateId);
        setData(response);

        // Animate score counter
        const targetScore = response?.feedback?.round_type === 'CODING' ? response?.feedback?.technical_readiness_score : response?.feedback?.session_feedback?.technical_readiness_score;
        if (targetScore) {
          const target = targetScore * 10;
          let start = 0;
          const duration = 1500;
          const increment = target / (duration / 16);

          const timer = setInterval(() => {
            start += increment;
            if (start >= target) {
              setAnimatedScore(target);
              clearInterval(timer);
            } else {
              setAnimatedScore(Math.floor(start));
            }
          }, 16);
        }
      } catch (error) {
        console.error("Error fetching feedback:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedback();

    const fetchProctoringData = async () => {
      if (!sessionId) return;
      try {
        const response = await ResultsClient.getProctoringData(sessionId, candidateId);
        setProctorData(response);
      } catch (error) {
        console.error("Error fetching proctoring data:", error);
      }
    };
    fetchProctoringData();
  }, [sessionId, candidateId]);

  const isCodingFeedback = data?.feedback?.round_type === 'CODING' || data?.feedback?.coding_evaluation != null;

  // Auto-select coding tab if it's purely a coding round
  useEffect(() => {
    if (isCodingFeedback && !data?.feedback?.session_feedback && activeTab === "overview") {
      setActiveTab("coding");
    }
  }, [isCodingFeedback, data?.feedback?.session_feedback, activeTab]);

  if (loading || reportLoading) {
    return <Loader isLoading={true} message={reportLoading ? "Generating your professional report..." : "Assembling your comprehensive report..."} />;
  }

  if (!data || !data.feedback) {
    return (
      <div className="error-state">
        <Info size={48} />
        <h2>Feedback not found</h2>
        <p>We couldn't retrieve the results for this session.</p>
        <button onClick={handleBack}>Back to Interviews</button>
      </div>
    );
  }

  const { feedback, session_history: history = [] } = data;
  const sessionFeedback = feedback.session_feedback || {};
  const overallScore = isCodingFeedback ? (feedback.technical_readiness_score * 10) : (sessionFeedback.technical_readiness_score * 10);
  const circumference = 2 * Math.PI * 110;
  const strokeDashoffset = circumference - (overallScore / 100) * circumference;

  const getTier = (score: number) => {
    if (score >= 80) return "High Performance Tier";
    if (score >= 60) return "Professional Tier";
    return "Needs Development";
  };

  return (
    <div className={`feedback-page-container ${isInline ? 'inline-stats-view' : ''}`}>
      {/* Top Bar */}
      {!isInline ? (
        <div className="admin-feedback-top-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button className="btn-share-results" onClick={handleBack} style={{ padding: '10px', borderRadius: '12px' }}>
              <ChevronLeft size={20} />
            </button>
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: '#0f172a', textTransform: 'capitalize' }}>
                {feedback.round_type?.replace(/_/g, ' ') || "Interview Performance"}
              </h2>
              <div style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>{feedback.session_name || 'Session Feedback'}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className="rs-header-nav" style={{ paddingBottom: '16px', display: 'flex', alignItems: 'center' }}>
          <button className="rs-btn-back" onClick={handleBack} style={{ padding: 0, background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#0f172a' }}>
            <ChevronLeft size={20} />
            <span style={{ fontSize: '18px', fontWeight: 700 }}>Detailed Feedback</span>
          </button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="feedback-tabs-nav">
        {[
          (!isCodingFeedback || sessionFeedback.technical_readiness_score) && { id: "overview", label: "Overview", icon: <Layout size={16} /> },
          isCodingFeedback && { id: "coding", label: "Coding", icon: <Code size={16} /> },
          feedback.technical_skills_evaluation && { id: "technical", label: "Technical", icon: <Code size={16} /> },
          feedback.behavioral_evaluation && { id: "behavioral", label: "Behavioral", icon: <Brain size={16} /> },
          feedback.core_competencies_report && { id: "competency", label: "Competencies", icon: <Award size={16} /> },
          feedback.hr_evaluation && { id: "hr", label: "HR", icon: <Users size={16} /> },
          feedback.hm_evaluation && { id: "hiringManager", label: "Manager", icon: <Briefcase size={16} /> },
          feedback.resume_score && { id: "resume", label: "Resume", icon: <FileText size={16} /> },
          feedback.resume_improvement?.length > 0 && { id: "improvements", label: "Improvements", icon: <TrendingUp size={16} /> },
          proctorData && { id: "proctoring", label: "Proctoring", icon: <Shield size={16} /> },
          data.video_url && { id: "video", label: "Video Recording", icon: <Video size={16} /> },
          { id: "transcript", label: "Transcript", icon: <MessageSquare size={16} /> }
        ].filter(Boolean).map((tab: any) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content-area">
        {activeTab === "overview" && (
          <div className="overview-tab-content">
            {/* Hero Section Moved Here */}
            <div className="feedback-header-hero">
              <div className="verdict-card">
                <div className="verdict-status">
                  <Award size={16} />
                  VERDICT: {feedback.final_verdict || sessionFeedback.final_verdict || "HIRE"}
                </div>
                <h1 style={{ textTransform: 'uppercase' }}>{feedback.round_type?.replace(/_/g, ' ') || "BEHAVIORAL"}</h1>
                <p>
                  {sessionFeedback.technical_readiness_justification || "Complete breakdown of your interview performance across all evaluated dimensions."}
                </p>
                <div className="verdict-actions">
                  <button className="btn-download-pdf" onClick={() => setShowDownloadModal(true)}>
                    <Download size={18} />
                    Download Full Report
                  </button>
                  <button className="btn-share-results" onClick={handleBack}>
                    <ChevronLeft size={18} />
                    Back to Stats
                  </button>
                </div>
              </div>

              <div className="score-hero-card">
                <div className="score-card-bg-decoration">
                  <div className="orb orb-1"></div>
                  <div className="orb orb-2"></div>
                  <div className="orb orb-3"></div>
                  <div className="score-card-grid"></div>
                </div>

                <div className="score-circle-container large-score">
                  <svg className="score-svg" viewBox="0 0 240 240">
                    <defs>
                      <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.95)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,1)" />
                      </linearGradient>
                      <filter id="scoreGlow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="6" result="blur" />
                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                      </filter>
                    </defs>
                    <circle className="score-bg-circle" cx="120" cy="120" r="110" />
                    <circle
                      className="score-fill-circle animated-ring"
                      cx="120" cy="120" r="110"
                      style={{
                        strokeDasharray: circumference,
                        strokeDashoffset: strokeDashoffset,
                        filter: 'url(#scoreGlow)',
                        stroke: 'url(#scoreGradient)'
                      }}
                    />
                    <circle className="score-glow-point" cx="120" cy="120" r="110" />
                  </svg>
                  <div className="score-text-overlay">
                    <span className="score-value">{animatedScore}</span>
                    <span className="score-label-hero" style={{ textTransform: 'uppercase', letterSpacing: '1px' }}>Readiness Score</span>
                  </div>
                </div>
                <div className="tier-badge-modern" style={{ textTransform: 'uppercase', letterSpacing: '1px' }}>
                  <Sparkles size={14} />
                  {getTier(overallScore)}
                </div>
              </div>
            </div>

            <div className="strength-card positive" style={{ marginBottom: '24px' }}>
              <h4><Award size={18} /> Key Strengths</h4>
              <ul>
                {sessionFeedback.key_strengths?.map((s: string, i: number) => <li key={i}>{s}</li>)}
              </ul>
            </div>

            <div className="summary-row">
              <div className="card-glass">
                <h3><MessageSquare size={18} /> Transcript Summary</h3>
                <p>{sessionFeedback.transcript_summary}</p>
              </div>
              <div className="card-glass">
                <h3><Users size={18} /> Communication Style</h3>
                <p>{sessionFeedback.communication_style_evaluation}</p>
              </div>
            </div>

            <div className="strength-card delta">
              <h4><Target size={18} /> Actionable Improvements</h4>
              <ul>
                {sessionFeedback.areas_for_improvement?.map((s: string, i: number) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          </div>
        )}

        {activeTab === "technical" && feedback.technical_skills_evaluation && (
          <div className="technical-tab-content">
            {/* Top Cards */}
            <div className="tech-top-cards">
              <div className="tech-coachability-card">
                <h3>Coachability Score</h3>
                <div className="tech-coachability-score">
                  <span className="tech-score-main">{feedback.technical_skills_evaluation.coachability_score}</span>
                  <span className="tech-score-sub">/10</span>
                </div>
              </div>
              <div className="tech-recruiter-card">
                <h3>Recruiter Note</h3>
                <p>{feedback.technical_skills_evaluation.recruiter_note}</p>
              </div>
            </div>

            {/* Detailed Skill Breakdown */}
            <h2 className="tech-breakdown-title">Detailed Skill Breakdown</h2>
            <div className="tech-skills-list">
              {feedback.technical_skills_evaluation.technical_scorecard.map((s: any, i: number) => {
                const isHigh = s.score >= 8;
                const isMed = s.score >= 5 && s.score < 8;

                let badgeClass = "tech-badge-low";
                let borderClass = "tech-border-low";
                if (isHigh) {
                  badgeClass = "tech-badge-high";
                  borderClass = "tech-border-high";
                } else if (isMed) {
                  badgeClass = "tech-badge-med";
                  borderClass = "tech-border-med";
                }

                return (
                  <div key={i} className={`tech-skill-card ${borderClass}`}>
                    <div className="tech-skill-header">
                      <h3 className="tech-skill-title">{s.skill}</h3>
                      <div className={`tech-skill-badge ${badgeClass}`}>
                        {s.score}/10
                      </div>
                    </div>

                    <div className="tech-skill-body">
                      <div className="tech-skill-col">
                        <label>SCENARIO</label>
                        <p className="tech-skill-scenario">"{s.initial_scenario}"</p>
                      </div>
                      <div className="tech-skill-col">
                        <label>OUTCOME</label>
                        <p className={`tech-skill-outcome ${badgeClass}-text`}>{s.outcome}</p>

                        <div className="tech-skill-hints">
                          <label>HINTS NEEDED</label>
                          <span className="tech-hints-val">{s.nudges_count ?? 0}</span>
                        </div>
                      </div>
                    </div>

                    {s.failed_justification && (
                      <div className="tech-skill-warning">
                        <span style={{ marginTop: '2px' }}>⚠️</span>
                        <span>{s.failed_justification}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === "behavioral" && feedback.behavioral_evaluation && (
          <div className="behavioral-tab-content">
            <div className="scores-grid">
              {[
                { label: "Initial Reaction", val: feedback.behavioral_evaluation.initial_reaction, max: 5, theme: 'theme-blue', icon: <Sparkles size={20} /> },
                { label: "Stakeholder Awareness", val: feedback.behavioral_evaluation.stakeholder_awareness, max: 5, theme: 'theme-indigo', icon: <Users size={20} /> },
                { label: "Resolution Logic", val: feedback.behavioral_evaluation.resolution_logic, max: 5, theme: 'theme-orange', icon: <Brain size={20} /> }
              ].map((stat, i) => (
                <div key={i} className={`premium-score-card ${stat.theme}`}>
                  <div className="card-top-row">
                    <div className="card-icon-container">{stat.icon}</div>
                    <div className="card-score-value">{Math.round((stat.val / stat.max) * 100)}%</div>
                  </div>
                  <h3>{stat.label}</h3>
                  <p>Evaluation of your behavioral performance in this specific dimension.</p>
                  <div className="card-progress-track">
                    <div className="card-progress-fill" style={{ width: `${(stat.val / stat.max) * 100}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="card-glass" style={{ marginTop: '20px' }}>
              <h3>Justification & Tags</h3>
              <p>{feedback.behavioral_evaluation.justification}</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
                {feedback.behavioral_evaluation.behavioral_tags?.map((t: string, i: number) => (
                  <span key={i} style={{ background: '#f1f5f9', color: '#475569', padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 600 }}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "hr" && feedback.hr_evaluation && (
          <div className="hr-tab-content">
            <div className="scores-grid">
              {[
                { label: "Motivation", val: feedback.hr_evaluation.motivation_score, theme: 'theme-emerald', icon: <Target size={20} /> },
                { label: "Self-Awareness", val: feedback.hr_evaluation.self_awareness_score, theme: 'theme-indigo', icon: <Brain size={20} /> },
                { label: "Operational Fit", val: feedback.hr_evaluation.operational_fit_score, theme: 'theme-blue', icon: <Users size={20} /> }
              ].map((stat, i) => (
                <div key={i} className={`premium-score-card ${stat.theme}`}>
                  <div className="card-top-row">
                    <div className="card-icon-container">{stat.icon}</div>
                    <div className="card-score-value">{stat.val * 10}%</div>
                  </div>
                  <h3>{stat.label}</h3>
                  <p>HR evaluation score for {stat.label.toLowerCase()} based on your interview answers.</p>
                  <div className="card-progress-track">
                    <div className="card-progress-fill" style={{ width: `${stat.val * 10}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="card-glass" style={{ marginTop: '24px' }}>
              <h3>HR Narrative</h3>
              <p>{feedback.hr_evaluation.hr_narrative}</p>
            </div>
          </div>
        )}

        {activeTab === "hiringManager" && feedback.hm_evaluation && (
          <div className="hm-tab-content">
            <div className="scores-grid">
              {[
                { label: "Impact Velocity", val: feedback.hm_evaluation.impact_velocity_score, theme: 'theme-blue', icon: <Sparkles size={20} /> },
                { label: "Managerial Friction", val: 10 - feedback.hm_evaluation.managerial_friction_score, theme: 'theme-orange', icon: <Info size={20} /> }
              ].map((stat, i) => (
                <div key={i} className={`premium-score-card ${stat.theme}`}>
                  <div className="card-top-row">
                    <div className="card-icon-container">{stat.icon}</div>
                    <div className="card-score-value">{Math.round(stat.val * 10)}%</div>
                  </div>
                  <h3>{stat.label}</h3>
                  <p>{stat.label === "Impact Velocity" ? "How quickly you can start contributing to the team." : "Inverted score showing potential ease of management."}</p>
                  <div className="card-progress-track">
                    <div className="card-progress-fill" style={{ width: `${stat.val * 10}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="card-glass" style={{ marginTop: '24px' }}>
              <h3>Recommendation</h3>
              <p>{feedback.hm_evaluation.hm_recommendation}</p>
            </div>
          </div>
        )}

        {activeTab === "competency" && feedback.core_competencies_report && (
          <div className="competency-tab-content">
            <div className="comp-summary-cards">
              {/* Overall Score */}
              <div className="comp-score-card">
                <h4>OVERALL COMPETENCY SCORE</h4>
                <div className="comp-score-ring">
                  <svg viewBox="0 0 36 36" className="circular-chart blue">
                    <path className="circle-bg"
                      d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path className="circle"
                      strokeDasharray={`${(feedback.core_competencies_report.overall_competency_score || 0) * 10}, 100`}
                      d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <div className="comp-score-text">
                    <span className="main-score">{feedback.core_competencies_report.overall_competency_score || 0}</span>
                    <span className="sub-score">/10</span>
                  </div>
                </div>
                <p>Score based on cognitive capabilities.</p>
              </div>

              {/* Candidate Superpower */}
              <div className="comp-superpower-card">
                <h4><Zap size={16} /> Candidate Superpower</h4>
                <p className="comp-superpower-title">{feedback.core_competencies_report.candidate_superpower}</p>
                <p className="comp-superpower-desc">Demonstrated exceptional ability during the session.</p>
              </div>

              {/* Growth Area */}
              <div className="comp-growth-card">
                <h4><TrendingUp size={16} /> Growth Area</h4>
                <p className="comp-growth-title">{feedback.core_competencies_report.growth_area}</p>
                <p className="comp-growth-desc">Recommended focus area for future development.</p>
              </div>
            </div>

            <div className="comp-details-header">
              <h2>Detailed Competency Breakdown</h2>
            </div>

            <div className="comp-details-grid">
              {feedback.core_competencies_report.competency_evaluations.map((c: any, i: number) => {
                let icon = <BookOpen size={18} />;
                if (c.competency === "Constraint Resolution") icon = <Wrench size={18} />;
                else if (c.competency === "Information Discovery") icon = <Search size={18} />;
                else if (c.competency === "Self-Correction") icon = <Edit2 size={18} />;

                return (
                  <div key={i} className="comp-detail-card">
                    <div className="comp-detail-header">
                      <div className="comp-detail-title">
                        <div className="comp-icon">{icon}</div>
                        <h3>{c.competency}</h3>
                      </div>
                      <div className="comp-detail-badge">{c.score}/10</div>
                    </div>

                    <div className="comp-detail-scenario">
                      <label>SCENARIO ASKED</label>
                      <p>"{c.scenario_asked}"</p>
                    </div>

                    <div className="comp-detail-notes">
                      <div className="comp-notes-grid">
                        {c.problem_solving_notes && (
                          <div className="comp-note-card">
                            <div className="comp-note-header">
                              <Puzzle size={14} color="#3b82f6" />
                              <strong>Problem Solving</strong>
                            </div>
                            <p>{c.problem_solving_notes}</p>
                          </div>
                        )}
                        {c.resilience_notes && (
                          <div className="comp-note-card">
                            <div className="comp-note-header">
                              <Shield size={14} color="#3b82f6" />
                              <strong>Resilience</strong>
                            </div>
                            <p>{c.resilience_notes}</p>
                          </div>
                        )}
                        {c.professional_notes && (
                          <div className="comp-note-card">
                            <div className="comp-note-header">
                              <Briefcase size={14} color="#3b82f6" />
                              <strong>Professional</strong>
                            </div>
                            <p>{c.professional_notes}</p>
                          </div>
                        )}
                        {c.implementation_notes && (
                          <div className="comp-note-card">
                            <div className="comp-note-header">
                              <Wrench size={14} color="#3b82f6" />
                              <strong>Craftsmanship</strong>
                            </div>
                            <p>{c.implementation_notes}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {feedback.core_competencies_report.communication_tip && (
              <div className="comp-communication-tip">
                <div className="comp-tip-icon">
                  <Lightbulb size={24} />
                </div>
                <div className="comp-tip-content">
                  <div className="comp-tip-header">
                    <h4>Communication Tip for {feedback.first_name || 'Candidate'}</h4>
                    <span className="comp-tip-badge">STRATEGIC INSIGHT</span>
                  </div>
                  <p>"{feedback.core_competencies_report.communication_tip}"</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "resume" && feedback.resume_score && (
          <div className="resume-tab-content">
            <div className="premium-score-card" style={{ marginBottom: '24px', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ fontSize: '20px', marginBottom: '4px' }}>Resume Optimization Score</h3>
                <p style={{ fontSize: '14px', color: '#64748b' }}>Overall rating based on industry standards</p>
              </div>
              <div style={{ fontSize: '48px', fontWeight: 800, color: '#02507d' }}>
                {(feedback.resume_score as any).total || 0}<span style={{ fontSize: '20px', color: '#94a3b8' }}>/100</span>
              </div>
            </div>
            <div className="rgf-metrics-grid" style={{ marginBottom: '24px' }}>
              {[
                { key: 'readability', label: 'Readability', icon: <BookOpen size={18} />, color: '#2563eb' },
                { key: 'impact', label: 'Impact', icon: <Zap size={18} />, color: '#ef4444' },
                { key: 'conciseness', label: 'Conciseness', icon: <AlignLeft size={18} />, color: '#f59e0b' },
                { key: 'tech_clarity', label: 'Tech Clarity', icon: <Code size={18} />, color: '#6366f1' },
              ].map(m => {
                const val = (feedback.resume_score as any)[m.key] ?? 0;
                return (
                  <div key={m.key} className="rgf-metric-card">
                    <div className="rgf-metric-header">
                      <div className="rgf-metric-icon-row">
                        <div className="rgf-metric-icon" style={{ background: `${m.color}15`, color: m.color }}>{m.icon}</div>
                        <span className="rgf-metric-label">{m.label}</span>
                      </div>
                      <span className="rgf-metric-score" style={{ color: m.color }}>{val}/10</span>
                    </div>
                    <div className="rgf-metric-bar-track">
                      <div className="rgf-metric-bar-fill" style={{ width: `${val * 10}%`, background: m.color }} />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="card-glass">
              <h2 className="rgf-panel-title">Actionable Improvements</h2>
              <p className="rgf-panel-subtitle" style={{ marginBottom: '24px' }}>Key adjustments to strengthen your professional profile</p>
              <ul className="rgf-action-list">
                {feedback.resume_score.actionable_improvements?.map((tip: string, i: number) => (
                  <li key={i} className="rgf-action-item">
                    <span className="rgf-action-icon-wrap"><TrendingUp size={16} /></span>
                    <p className="rgf-action-text">{tip}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {activeTab === "improvements" && (
          <div className="improvements-tab-content">
            <div className="card-glass">
              <h2 className="rgf-panel-title">Detailed Comparison</h2>
              <p className="rgf-panel-subtitle">Optimized phrasing for your experience sections</p>
              <div style={{ marginTop: '20px' }}>
                {feedback.resume_improvement?.map((imp: any, i: number) => (
                  <div key={i} className="rgf-diff-block">
                    <div className="rgf-diff-cols">
                      <div className="rgf-diff-col rgf-original">
                        <span className="rgf-diff-tag rgf-tag-original">ORIGINAL</span>
                        <p>"{imp.original}"</p>
                      </div>
                      <div className="rgf-diff-col rgf-improved">
                        <span className="rgf-diff-tag rgf-tag-improved">IMPROVED</span>
                        <p>"{imp.improvement}"</p>
                      </div>
                    </div>
                    <div className="rgf-diff-reason">
                      <Lightbulb size={14} className="rgf-reason-icon" />
                      <span><strong>Reason:</strong> {imp.reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "video" && data.video_url && (
          <div className="video-tab-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {showVideoNote && (
              <div className="video-note" style={{ background: '#f0fdf4', padding: '16px 20px', borderRadius: '12px', border: '1px solid #bbf7d0', color: '#065f46', display: 'flex', alignItems: 'flex-start', maxWidth: '100%' }}>
                <Info size={20} style={{ marginRight: '12px', marginTop: '2px', flexShrink: 0, color: '#059669' }} />
                <span style={{ fontSize: '14.5px', lineHeight: '1.5', flexGrow: 1 }}><strong>Note:</strong> Video recordings are only available for <strong>30 days</strong> after the session. Please download the recording if you need a permanent copy.</span>
                <button onClick={() => setShowVideoNote(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#34d399', padding: 0, marginLeft: '12px' }}>
                  <X size={18} />
                </button>
              </div>
            )}

            <div className="card-glass" style={{ width: '50%', minWidth: '400px', padding: '24px' }}>
              <div className="video-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Video size={22} color="#059669" />
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Session Recording</h3>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 500, color: '#475569' }}>
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }}></span>
                    Processed & Secure
                </div>
              </div>
              
              <div 
                className="video-thumbnail-container" 
                style={{ 
                  borderRadius: '12px', 
                  overflow: 'hidden', 
                  background: '#1e293b', 
                  height: '260px',
                  display: 'flex', 
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  marginBottom: '20px',
                }}
                onClick={() => setShowVideoModal(true)}
              >
                <video 
                  src={data.video_url} 
                  preload="metadata" 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', background: 'rgba(0,0,0,0.3)' }}>
                  <div style={{ width: '64px', height: '48px', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.3)', transition: 'transform 0.2s ease-out' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <Play size={24} color="#ffffff" fill="currentColor" />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '6px' }}>Availability</span>
                  {data.session_created_at ? (
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={16} />
                        {calculateDaysRemaining(data.session_created_at)} Days Remaining
                    </span>
                  ) : (
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#64748b' }}>Unknown</span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button onClick={() => window.open(data.video_url, '_blank')} style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', fontSize: '13px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <Download size={16} /> Download
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "proctoring" && proctorData && (
          <div className="proctoring-tab-content">
            <div className="proctor-header-row">
              <div className="proctor-score-card">
                <div className="proctor-score-circle">
                  <div className="score-val">{proctorData.ai_report?.integrity_summary?.integrity_score ?? 0}</div>
                  <div className="score-label">Integrity</div>
                </div>
                <div className="proctor-status-tag" data-status={proctorData.ai_report?.integrity_summary?.overall_status}>
                  {proctorData.ai_report?.integrity_summary?.overall_status}
                </div>
              </div>
              <div className="proctor-recommendation-card card-glass">
                <h3><Brain size={18} /> AI Integrity Recommendation</h3>
                <p>{proctorData.ai_report?.ai_recommendation}</p>
                <button className="btn-download-small" onClick={handleDownloadProctoringReport}>
                  <Download size={14} /> Download Proctoring PDF
                </button>
              </div>
            </div>            {proctorData.ai_report ? (
              <>
                <div className="proctor-behavior-row" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem', marginBottom: '2rem' }}>
                  {/* Left Column: Behavioral Analysis */}
                  <div className="card-glass sentiment-summary" style={{ padding: '2rem', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                      <Brain size={22} color="#2563eb" />
                      <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b', fontWeight: 700 }}>Behavioral Analysis</h4>
                    </div>
                    
                    {proctorData.ai_report?.analysis_summary && (
                      <div className="analysis-summary-narrative" style={{ marginBottom: '2rem', padding: '1.25rem', background: '#f8fafc', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                        <p style={{ color: '#334155', fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>
                          {proctorData.ai_report.analysis_summary}
                        </p>
                      </div>
                    )}

                    <div className="sentiment-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', padding: '1.5rem', background: '#ffffff', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                      <div className="sentiment-stat">
                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '6px' }}>Dominant Sentiment</label>
                        <span style={{ fontSize: '1rem', color: '#1e293b', fontWeight: 600 }}>{proctorData.ai_report?.sentiment_summary?.dominant_sentiment}</span>
                      </div>
                      <div className="sentiment-stat">
                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '6px' }}>Engagement Score</label>
                        <span style={{ fontSize: '1rem', color: '#2563eb', fontWeight: 700 }}>{proctorData.ai_report?.sentiment_summary?.average_engagement_score}/10</span>
                      </div>
                      <div className="sentiment-stat">
                        <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '6px' }}>Engagement Trend</label>
                        <span style={{ fontSize: '1rem', color: '#1e293b', fontWeight: 600 }}>{proctorData.ai_report?.sentiment_summary?.engagement_trend}</span>
                      </div>
                    </div>

                    {proctorData.ai_report?.sentiment_summary?.key_observations && (
                      <div className="key-obs" style={{ marginTop: '2rem' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: '#1e293b', fontWeight: 700, marginBottom: '1rem' }}>Detailed Observations:</label>
                        <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {proctorData.ai_report.sentiment_summary.key_observations.map((obs: string, i: number) => (
                            <li key={i} style={{ fontSize: '0.9rem', color: '#475569', paddingLeft: '1.5rem', position: 'relative', lineHeight: 1.5 }}>
                              <span style={{ position: 'absolute', left: 0, color: '#3b82f6' }}>•</span> {obs}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Right Column: Integrity Analysis */}
                  <div className="card-glass risk-summary" style={{ padding: '2rem', border: '1px solid #e2e8f0', background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.5rem' }}>
                      <Shield size={22} color="#ef4444" />
                      <h4 style={{ margin: 0, fontSize: '1.1rem', color: '#1e293b', fontWeight: 700 }}>Integrity Risks</h4>
                    </div>
                    
                    <div style={{ padding: '1rem', background: proctorData.ai_report?.integrity_summary?.overall_status === 'Pass' ? '#f0fdf4' : '#fef2f2', borderRadius: '12px', border: '1px solid', borderColor: proctorData.ai_report?.integrity_summary?.overall_status === 'Pass' ? '#dcfce7' : '#fee2e2', marginBottom: '1.5rem' }}>
                       <p style={{ color: proctorData.ai_report?.integrity_summary?.overall_status === 'Pass' ? '#166534' : '#991b1b', fontSize: '0.9rem', margin: 0, fontWeight: 500 }}>
                        {proctorData.ai_report?.integrity_summary?.risk_summary}
                      </p>
                    </div>

                    <div className="anomaly-counts" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                      <div className="count-item critical" style={{ textAlign: 'center', padding: '1rem', background: '#fef2f2', borderRadius: '12px' }}>
                        <span className="count-val" style={{ display: 'block', fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>{proctorData.ai_report?.integrity_summary?.critical_count}</span>
                        <span className="count-label" style={{ fontSize: '0.7rem', color: '#ef4444', textTransform: 'uppercase', fontWeight: 700 }}>Critical</span>
                      </div>
                      <div className="count-item" style={{ textAlign: 'center', padding: '1rem', background: '#f8fafc', borderRadius: '12px' }}>
                        <span className="count-val" style={{ display: 'block', fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>{proctorData.ai_report?.integrity_summary?.total_anomalies}</span>
                        <span className="count-label" style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700 }}>Issues</span>
                      </div>
                    </div>

                    <div style={{ padding: '1.25rem', background: '#f8fafc', borderRadius: '16px', border: '1px solid #f1f5f9' }}>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px', marginBottom: '8px' }}>Security Recommendation</label>
                      <p style={{ color: '#475569', fontSize: '0.85rem', lineHeight: 1.5, margin: 0 }}>
                        {proctorData.ai_report?.ai_recommendation}
                      </p>
                    </div>
                  </div>
                </div>

                {proctorData.ai_report?.malpractice_logs?.filter((l: any) => l.status === 'Critical').length > 0 && (
                  <div className="malpractice-log-section">
                    <h3>Malpractice Incident Log</h3>
                    <div className="malpractice-grid">
                      {proctorData.ai_report.malpractice_logs.filter((l: any) => l.status === 'Critical').map((log: any, i: number) => (
                        <div key={i} className="malpractice-card critical">
                          <div className="malpractice-image">
                            <img src={log.frame_url} alt="Evidence" onError={(e) => {
                              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=Evidence+Image';
                            }} />
                            <div className="malpractice-badge">{log.status}</div>
                          </div>
                          <div className="malpractice-details">
                            <div className="log-time">{new Date(log.timestamp).toLocaleTimeString()}</div>
                            <div className="log-flags">
                              {log.flags?.map((f: string, fi: number) => <span key={fi}>{f}</span>)}
                            </div>
                            <p className="log-evidence">{log.evidence}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "3rem 1rem", background: "rgba(255, 255, 255, 0.02)", borderRadius: "20px", border: "1px dashed rgba(255, 255, 255, 0.1)", marginTop: "2rem" }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "1rem", opacity: 0.5 }}>🛡️</div>
                <h5 style={{ color: "#f8fafc", fontSize: "1.1rem", marginBottom: "0.5rem" }}>Proctor Report Under Evaluation</h5>
                <p style={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "0.95rem", maxWidth: "400px", margin: "0 auto", lineHeight: 1.5 }}>
                  We are evaluating the proctor report and will share it soon. The comprehensive integrity and behavioral analysis is being processed.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "coding" && feedback.coding_evaluation && (
          <div className="coding-tab-content">
            <div className="coding-eval-header">
              <div className="coding-eval-score-card">
                <h3>Overall Coding Score</h3>
                <div className="coding-eval-score-value">
                  <span className="coding-score-main">{feedback.coding_evaluation.overall_score}</span>
                  <span className="coding-score-sub">/10</span>
                </div>
              </div>
              <div className="coding-eval-verdict-card">
                <h3>Verdict</h3>
                <span className={`coding-verdict-badge ${(feedback.coding_evaluation.final_verdict || '').toLowerCase() === 'hire' ? 'verdict-hire' : 'verdict-no-hire'}`}>
                  {feedback.coding_evaluation.final_verdict}
                </span>
              </div>
            </div>

            <div className="coding-summary-card">
              <p>{feedback.coding_evaluation.summary}</p>
            </div>

            <div className="coding-strengths-row">
              <div className="coding-strength-card positive">
                <h4>Key Strengths</h4>
                <ul>
                  {feedback.coding_evaluation.key_strengths?.map((s: string, i: number) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <div className="coding-strength-card delta">
                <h4>Areas for Improvement</h4>
                <ul>
                  {feedback.coding_evaluation.areas_for_improvement?.map((s: string, i: number) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            </div>

            <h2 className="coding-breakdown-title">Question Scorecards</h2>
            <div className="coding-question-list">
              {(feedback.coding_evaluation.question_scorecards || []).map((q: any, i: number) => {
                const isSql = q.programming_language === 'SQL';
                const isCorrect = q.is_correct;
                return (
                  <div key={i} className={`coding-question-card ${isCorrect ? 'q-correct' : 'q-incorrect'}`}>
                    <div className="coding-q-header">
                      <div className="coding-q-title-row">
                        <span className="coding-q-seq">Q{q.seq_no + 1}</span>
                        <h3 className="coding-q-title">{q.question_title}</h3>
                      </div>
                      <div className="coding-q-badges">
                        <span className={`coding-lang-badge ${isSql ? 'lang-sql' : ''}`}>{q.programming_language}</span>
                        <span className="coding-diff-badge">{q.difficulty_level}</span>
                        <span className={`coding-correct-badge ${isCorrect ? 'correct' : 'incorrect'}`}>
                          {isCorrect ? 'Correct' : 'Incorrect'}
                        </span>
                      </div>
                    </div>

                    <div className="coding-q-body">
                      <div className="coding-q-code">
                        <label>Submitted Code</label>
                        <pre><code>{q.code_content}</code></pre>
                      </div>

                      {q.feedback && (
                        <div className="coding-q-feedback">
                          {isSql ? (
                            <>
                              <div className="coding-feedback-grid">
                                <div className="coding-fb-item">
                                  <span className="coding-fb-label">Correctness</span>
                                  <span className="coding-fb-val">{q.feedback.correctness_score ?? '-'}/100</span>
                                </div>
                                <div className="coding-fb-item">
                                  <span className="coding-fb-label">Performance</span>
                                  <span className="coding-fb-val">{q.feedback.performance_score ?? '-'}/100</span>
                                </div>
                                <div className="coding-fb-item">
                                  <span className="coding-fb-label">Final Score</span>
                                  <span className="coding-fb-val">{q.feedback.final_score ?? '-'}/100</span>
                                </div>
                                <div className="coding-fb-item">
                                  <span className="coding-fb-label">Execution Time</span>
                                  <span className="coding-fb-val">{q.feedback.candidate_execution_time_ms ?? '-'}ms</span>
                                </div>
                              </div>
                              <p className="coding-fb-summary">{q.feedback.feedback_summary}</p>
                              {q.feedback.suggestions?.length > 0 && (
                                <div className="coding-fb-suggestions">
                                  <label>Suggestions</label>
                                  <ul>{q.feedback.suggestions.map((sg: string, si: number) => <li key={si}>{sg}</li>)}</ul>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              {q.feedback.feedback_summary && (
                                <p className="coding-fb-summary">{q.feedback.feedback_summary}</p>
                              )}
                              {q.feedback.suggestions?.length > 0 && (
                                <div className="coding-fb-suggestions">
                                  <label>Suggestions</label>
                                  <ul>{q.feedback.suggestions.map((sg: string, si: number) => <li key={si}>{sg}</li>)}</ul>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {q.execution_result && (
                        <div className="coding-q-execution">
                          <label>Execution Output</label>
                          <pre><code>{typeof q.execution_result === 'string' ? q.execution_result : JSON.stringify(q.execution_result, null, 2)}</code></pre>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === "transcript" && (
          <div className="transcript-tab-content">
            <div className="transcript-chat">
              {history.map((msg: any, i: number) => (
                <div key={i} className={`chat-bubble ${msg.role === 'candidate' ? 'candidate' : 'bot'}`}>
                  <label>
                    {msg.role === 'candidate' ? 'You' : 'JozAI'}
                    {msg.timestamp && <span style={{ marginLeft: '8px', fontSize: '11px', color: '#64748b' }}>{new Date(msg.timestamp.endsWith('Z') ? msg.timestamp : msg.timestamp + 'Z').toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                  </label>
                  <p>{msg.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      


      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        loading={reportLoading}
      />

      {showVideoModal && data?.video_url && (
        <Modal open={showVideoModal} onClose={() => setShowVideoModal(false)} title="Session Recording" width="800px">
          <div style={{ borderRadius: '12px', overflow: 'hidden', background: '#000', display: 'flex', justifyContent: 'center', height: '60vh', minHeight: '400px', maxHeight: '600px' }}>
            <video 
              controls 
              autoPlay
              src={data.video_url} 
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminFeedbackPage;
